import { Inject } from '@odinjs/odin';

import { Endpoint, ProviderAccreditationPendenciesService, ProviderAccreditationStatusService, Response, ToastService } from '@philips-emr/hpms-provider-commons';
import providerContext from '@philips-emr/hpms-provider-commons/lib/context/provider-context';
import { HPMSOnClickTermUse, HpmsTermServiceAPI } from '@philips-emr/hpms-provider-components';
import { ButtonAPI, FetchAPI, FieldAPI, FormAPI, ModalAPI, PanelAPI, RepeaterAPI } from '@philips-emr/tws-components';
import { ToastLevel } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnCancel, OnClick, OnClose, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { MountEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { AccreditationConclusionAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

import { AccreditationStatus } from './accreditation-update-controller';

interface DataTerm {
  nameTerm: string;
  term: string;
}

interface CancellationReason {
  nrSequenceCancellationReason: number,
  dsCancellationReason: string,
}

const PENDENCIES = 'P';
@Controller({ fragment: FragmentNames.ACCREDITATION_CONCLUSION })
class AccreditationConclusionController extends BaseController {
  @Inject(Aliases.PANEL_PENDENCIES)
  private panelPendencies: PanelAPI;
  @Inject(Aliases.PANEL_TERM)
  private panelTerm: PanelAPI;
  @Inject(Aliases.HPMS_TERM_SERVICE)
  private hpmsTermService: HpmsTermServiceAPI;
  @Inject(Aliases.FIELD_TERM)
  private fieldTerm: FieldAPI;
  @Inject(Aliases.MODAL_TERM_FORM)
  private modalTerm: ModalAPI;
  @Inject(Aliases.BUTTON_SEND)
  private buttonSend: ButtonAPI;
  @Inject(Aliases.PANEL_LOADER)
  private panelLoader: PanelAPI;
  @Inject(Aliases.PANEL_LOADER_PENDENCIES)
  private panelLoaderPendencies: PanelAPI;
  @Inject(Aliases.REPEATER_PENDENCIES)
  private repeater: RepeaterAPI;
  @Inject(Aliases.PANEL_CANCEL)
  private panelCancel: PanelAPI;
  @Inject(Aliases.FORM_CANCEL_REQUEST)
  private formCancelRequest: FormAPI;
  @Inject(Aliases.BUTTON_CANCEL_REQUEST)
  private buttonCancelRequest: ButtonAPI;

  @OnMount(Aliases.PANEL_CONCLUSION)
  onMountPanelConclusion(): void {
    const { nrSeqTypeAccreditation, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    HttpService.get(Endpoint.GET.TERM_TYPE_ACCREDITATION, { nrSeqTypeAccreditation, nrSeqProvider })
      .then(resp => {
        const data = resp.body as string;
        if (data.length == 0) {
          this.fieldTerm.setVisible(false);
        }
      })
      .catch(() => ToastService.open({ message: MessageService.translate('provider.error-process-request'), level: ToastLevel.ERROR }));

    ProviderAccreditationPendenciesService.getPendencies(Number(nrSeqAccreditation), Number(nrSeqProvider), PENDENCIES)
      .then((response) => {
        if (isEmptyArrayOrObject(response.body)) {
          this.panelPendencies.setVisible(false);
        } else {
          this.buttonSend.setEnabled(false);
        }
      })
      .catch((response) => ToastService.open(response));
  }

  @OnMount(Aliases.REPEATER_PENDENCIES)
  onMountRepeater(): void {
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    ProviderAccreditationPendenciesService.getPendencies(Number(nrSeqAccreditation), Number(nrSeqProvider), PENDENCIES)
      .then((response) => {
        if (!isEmptyArrayOrObject(response.body)) {
          this.repeater.setData(response.body as unknown as Record<string, Value>[]);
        }
        this.panelLoaderPendencies.setVisible(false);
      })
      .catch((response) => ToastService.open(response));
  }

  @OnMount([Aliases.BUTTON_SEND, Aliases.BUTTON_CANCEL_REQUEST])
  onMountButtom(event: MountEvent): void {
    const buttom = event.target as ButtonAPI;
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    ProviderAccreditationStatusService.getAccreditationStatus(Number(nrSeqAccreditation), Number(nrSeqProvider))
      .then((response) => {
        const { status } = response.body as unknown as Response;
        this.controlVisibilityButtons(buttom, status);
      })
      .catch((response) => ToastService.open(response));
  }

  private controlVisibilityButtons(buttom: ButtonAPI, status: string): void {
    if (buttom.getAlias() === Aliases.BUTTON_CANCEL_REQUEST) {
      buttom.setVisible(status == AccreditationStatus.AWAITING_REQUESTER);
    } else {
      buttom.setEnabled(status == AccreditationStatus.AWAITING_REQUESTER || status == AccreditationStatus.AWAITING_REVISION);
    }
  }

  @OnMount(Aliases.BUTTON_CANCEL_REQUEST)
  onMountButtomCancel(event: MountEvent): void {
    const buttom = event.target as ButtonAPI;
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    ProviderAccreditationStatusService.getAccreditationStatus(Number(nrSeqAccreditation), Number(nrSeqProvider))
      .then((response) => {
        const { status } = response.body as unknown as Response;
        buttom.setEnabled(status == AccreditationStatus.AWAITING_REQUESTER);
      })
      .catch((response) => ToastService.open(response));
  }

  @OnMount(Aliases.FETCH_FINAL_MESSAGE)
  asynconMountFetch(event: MountEvent): void {
    const api = (event.target as unknown) as FetchAPI;
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    api.setParameters({ nrSeqProvider });
    api.forceFetch();
  }

  @HPMSOnClickTermUse(Aliases.HPMS_TERMS_OF_USE)
  onClickTermCheckbox(): void {
    const { nrSeqTypeAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    HttpService.get(Endpoint.GET.TERM_TYPE_ACCREDITATION, { nrSeqProvider, nrSeqTypeAccreditation })
      .then(resp => {
        const { nameTerm } = resp.body as DataTerm;
        const data = resp.body as string;
        this.panelTerm.setVisible(true);
        this.hpmsTermService.setData(data);
        this.panelTerm.setVisible(true);
        this.modalTerm.setTitle(nameTerm);
      })
      .catch((response) => ToastService.open(response));
  }

  @OnClick(Aliases.BUTTON_SEND)
  onClickButtonSend(): void {
    this.panelLoader.setVisible(true);
    this.buttonCancelRequest.setVisible(false);
    this.buttonSend.setEnabled(false);
    const { nrSeqAccreditation, nrSeqTypeAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    HttpService.post(Endpoint.POST.ACCREDITATION_SEND, { nrSeqProvider, nrSeqAccreditation, nrSeqTypeAccreditation })
      .then((resp) => {
        const { response } = resp.body as Response;
        this.panelLoader.setVisible(false);
        this.buttonCancelRequest.setVisible(false);
        ToastService.open({ message: response, level: ToastLevel.SUCCESS });
      })
      .catch((resp) => {
        this.buttonCancelRequest.setVisible(true);
        this.buttonSend.setEnabled(true);
        const { response } = resp.body as unknown as Response;
        ToastService.open({ message: response, level: ToastLevel.ERROR });
        this.panelLoader.setVisible(false);
      });
  }

  @OnClose(Aliases.MODAL_TERM_FORM)
  closeModal(): void {
    this.panelTerm.setVisible(false);
  }

  @OnClick(Aliases.BUTTON_CANCEL_REQUEST)
  onClickCancel(): void {
    this.panelCancel.setVisible(true);
  }

  @OnClose(Aliases.MODAL_REASON_FOR_CANCELING_ACCREDITATION)
  onCloseModalCancel(): void {
    this.panelCancel.setVisible(false);
  }

  @OnCancel(Aliases.FORM_CANCEL_REQUEST)
  onCancelForm(): void {
    this.panelCancel.setVisible(false);
  }

  @OnSubmit(Aliases.FORM_CANCEL_REQUEST)
  onSubmitFormCancelRequest(event: FormAPI): void {
    const { record } = event;
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    const { cancellationReason } = record;
    const { nrSequenceCancellationReason } = cancellationReason as CancellationReason;
    this.formCancelRequest.setEditing(false);
    this.formCancelRequest.setLoading(true);
    this.buttonSend.setEnabled(false);
    HttpService.post(Endpoint.POST.CANCELING_ACCREDITATION, { nrSeqProvider, nrSeqAccreditation, nrSequenceCancellationReason })
      .then((resp) => {
        const message = resp.body as string;
        this.buttonCancelRequest.setVisible(false);
        ToastService.open({ message: message, level: ToastLevel.SUCCESS });
        this.formCancelRequest.setLoading(false);
        this.panelCancel.setVisible(false);
      })
      .catch((response) => {
        this.buttonCancelRequest.setVisible(true);
        this.formCancelRequest.setLoading(false);
        this.formCancelRequest.setEditing(true);
        ToastService.open(response);
      });
  }
}

export {
  AccreditationConclusionController,
};
